import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/v5/lib/api";
import {
  EMPTY_PATIENT_DETAIL,
  normalizePatientAppointments,
  normalizePatientMemberships,
  normalizePatientTags,
  type PatientDetail,
} from "@/lib/patientDetail";

interface LaravelEnvelope<T> { data: T; meta: unknown; }

export function usePatientDetail(patientId: string | null) {
  const [detail, setDetail] = useState<PatientDetail>(EMPTY_PATIENT_DETAIL);
  const [loading, setLoading] = useState(false);
  // Sequence guard: only the most recent request may apply its result. Without
  // this, rapidly switching patients (e.g. triaging the message inbox) could let
  // a slower earlier fetch resolve AFTER a later one and clobber `detail` with a
  // previously-viewed patient — which drove SMS to be delivered to the wrong
  // patient. Mirrors the `requestSeqRef` guard in MessagesPage.loadMessages.
  const requestSeqRef = useRef(0);

  const refetch = useCallback(async () => {
    if (!patientId) return;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const base = `/patients/${patientId}`;
      const [patientRes, tagsRes, noteRes, templatesRes, memRes, apptRes] =
        await Promise.all([
          api<LaravelEnvelope<Record<string, unknown>>>(base).catch(() => null),
          api<LaravelEnvelope<{ patientsTagsCustomID: number; title: string }[]>>(`${base}/tags`).catch(() => null),
          api<LaravelEnvelope<{ patientID: number; note: string }>>(`${base}/primarynote`).catch(() => null),
          api<LaravelEnvelope<{ membershipTemplateID: string; title: string }[]>>(
            "/memberships",
          ).catch(() => null),
          api<LaravelEnvelope<{ membershipTemplateID: string; paymentThroughDate?: string }[]>>(
            `${base}/memberships?active=true`,
          ).catch(() => null),
          api<LaravelEnvelope<{ startTime: string; cancelled?: boolean; [k: string]: unknown }[]>>(
            `${base}/appointments`,
          ).catch(() => null),
        ]);

      // A newer patient was selected while this request was in flight — drop
      // the stale response so it can't overwrite the current patient's detail.
      if (seq !== requestSeqRef.current) return;

      const patient = patientRes?.data ?? null;
      const tags = normalizePatientTags(tagsRes);
      const noteObj = noteRes?.data ?? null;
      const memberships = normalizePatientMemberships(memRes, templatesRes);
      const { upcomingAppointments, recentAppointments } = normalizePatientAppointments(apptRes);

      setDetail({
        patient: patient as Record<string, unknown> | null,
        tags,
        note: (noteObj as { note?: string } | null)?.note ?? "",
        memberships,
        upcomingAppointments,
        recentAppointments,
      });
    } catch (err) {
      if (seq === requestSeqRef.current) {
        console.error("Failed to fetch patient detail:", err);
      }
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [patientId]);

  useEffect(() => {
    setDetail(EMPTY_PATIENT_DETAIL);
    refetch();
  }, [refetch]);

  return { detail, loading };
}
